import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ArrowBack, DeleteOutline, EditOutlined, Publish } from "@mui/icons-material";
import {
    fetchQuestion,
    fetchAnswerSet,
    createQuestion,
    updateQuestion,
    publishQuestion,
    deleteQuestion,
    fetchAnswerSets,
    fetchQuestionsPaginated,
} from "../../../utils/api";
import { InclusionMode, QuestionType, type Question } from "../../../types/rubric";
import type { AnswerSet, AnswerValue } from "../../../types/answerSet";
import theme from "../../../theme/Theme";
import RubricManagementList, { Column, type StatusFilter } from "../../molecules/RubricManagementList";
import { useListState } from "../../../hooks/useListState";
import ExpressionBuilder from "./CodeWorkflow/ExpressionBuilder";
import { ConfirmationDialog } from "./ConfirmationDialog";

type ViewMode = "list" | "detail";

interface QuestionFormState {
    name: string;
    questionText: string;
    questionType: Question["questionType"];
    required: boolean;
    answerSetId: number | null;
    displayOrder?: number | null;
    active: boolean;
    selectedAnswerIds: number[];
    inclusionMode: InclusionMode;
    expression: string;
}

interface QuestionsPanelProps {
    onPathChange?: (path: string) => void;
    onRequestNavigate?: (tab: "answerSets") => void;
}

const questionTypeOptions: { value: Question["questionType"]; label: string }[] = [
    { value: QuestionType.MULTIPLE_CHOICE, label: "Multiple Choice" },
    { value: QuestionType.FREE_TEXT, label: "Free Text" },
    { value: QuestionType.SYSTEM, label: "System" },
    { value: QuestionType.SPAN, label: "Span (Single-select)" },
    { value: QuestionType.SPANS, label: "Spans (Multi-select)" },
];

const initialFormState: QuestionFormState = {
    name: "",
    questionText: "",
    questionType: QuestionType.MULTIPLE_CHOICE,
    required: true,
    answerSetId: null,
    displayOrder: undefined,
    active: true,
    selectedAnswerIds: [],
    inclusionMode: InclusionMode.EXCLUDE,
    expression: "",
};

// Column configuration for the questions list - single flowing layout
const questionColumns: Column<Question>[] = [
    {
        field: "name",
        label: "Name",
        sortable: true,
        width: "flex",
        render: (item) => (
            <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body1">
                        {item.name}
                    </Typography>
                    <Chip label={item.questionType} size="small" color="primary" variant="outlined" />
                    {item.status && (
                        <Chip
                            label={item.status === "PUBLISHED" ? "Published" : "Draft"}
                            size="small"
                            color={item.status === "PUBLISHED" ? "success" : "warning"}
                            variant="outlined"
                        />
                    )}
                    {item.answerSetName && (
                        <Chip label={item.answerSetName} size="small" variant="outlined" />
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {item.questionText}
                </Typography>
                {item.answerSetName && item.questionType !== QuestionType.FREE_TEXT && (item.answers?.length ?? 0) > 0 && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        Example: {item.answers[0].name}
                    </Typography>
                )}
                {item.questionType === QuestionType.FREE_TEXT && item.answerSetName && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        Free Text - {item.answerSetName}
                    </Typography>
                )}
            </Box>
        ),
    },
];

const QuestionsPanel: React.FC<QuestionsPanelProps> = ({ onPathChange }) => {
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [questions, setQuestions] = useState<Question[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [answerSets, setAnswerSets] = useState<AnswerSet[]>([]);
    const [answerSetCache, setAnswerSetCache] = useState<Record<number, AnswerSet>>({});
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
    const [formValues, setFormValues] = useState<QuestionFormState>(initialFormState);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishingQuestionId, setPublishingQuestionId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<Question | null>(null);

    // Use URL-based list state for pagination, sorting, and search
    const listState = useListState({ defaultSortBy: "name", defaultSortDirection: "asc", paramPrefix: "q" });
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    useEffect(() => {
        const path =
            viewMode === "list"
                ? "Questions"
                : selectedQuestion
                    ? "Update Question"
                    : "Create Question";
        onPathChange?.(path);
    }, [viewMode, selectedQuestion, onPathChange]);

    const loadQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchQuestionsPaginated({
                page: listState.page,
                size: listState.pageSize,
                sortBy: listState.sortBy,
                sortDirection: listState.sortDirection,
                search: listState.search || undefined,
                active: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
            });
            setQuestions(response.content ?? []);
            setTotalCount(response.totalElements);
        } catch (err) {
            console.error("Failed to fetch questions", err);
            setError(err instanceof Error ? err.message : "Unable to load questions");
        } finally {
            setLoading(false);
        }
    }, [listState.page, listState.pageSize, listState.sortBy, listState.sortDirection, listState.search, statusFilter]);

    const loadAnswerSets = useCallback(async () => {
        try {
            const sets = await fetchAnswerSets({ active: true });
            setAnswerSets(sets ?? []);
        } catch (err) {
            console.error("Failed to fetch answer sets", err);
        }
    }, []);

    useEffect(() => {
        loadQuestions();
        loadAnswerSets();
    }, [loadQuestions, loadAnswerSets]);

    const resetForm = () => {
        setFormValues(initialFormState);
        setSelectedQuestion(null);
        setFormError(null);
    };

    const handleSelectQuestion = async (question: Question) => {
        setDetailLoading(true);
        setFormError(null);
        try {
            const detailed = await fetchQuestion(question.id);
            setSelectedQuestion(detailed);
            setFormValues({
                name: detailed.name ?? "",
                questionText: detailed.questionText ?? "",
                questionType: detailed.questionType ?? QuestionType.MULTIPLE_CHOICE,
                required: Boolean(detailed.required),
                answerSetId: detailed.answerSetId ?? null,
                displayOrder: detailed.displayOrder,
                active: detailed.active ?? true,
                selectedAnswerIds: detailed.selectedAnswerIds ?? [],
                inclusionMode: detailed.inclusionMode ?? InclusionMode.EXCLUDE,
                expression: detailed.expression ?? "",
            });
            setViewMode("detail");
            if (detailed.answerSetId && detailed.answers) {
                setAnswerSetCache((prev) => ({
                    ...prev,
                    [detailed.answerSetId as number]: {
                        id: detailed.answerSetId as number,
                        name: detailed.answerSetName ?? "",
                        description: "",
                        active: true,
                        answers: detailed.answers ?? [],
                    } as AnswerSet,
                }));
            }
            // If the question references an answer set not in the active list, fetch and add it
            if (detailed.answerSetId && !answerSets.some((set) => set.id === detailed.answerSetId)) {
                try {
                    const inactiveAnswerSet = await fetchAnswerSet(detailed.answerSetId);
                    setAnswerSets((prev) => [...prev, inactiveAnswerSet]);
                } catch (err) {
                    console.warn(`Could not fetch inactive answer set ${detailed.answerSetId}`, err);
                }
            }
        } catch (err) {
            console.error("Failed to load question", err);
            setError(err instanceof Error ? err.message : "Unable to load question");
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCreateNew = () => {
        resetForm();
        setViewMode("detail");
    };

    const handleBackToList = () => {
        setViewMode("list");
        resetForm();
    };

    const handleFieldChange = (field: keyof QuestionFormState, value: unknown) => {
        setFormValues((prev) => {
            const newValues = {
                ...prev,
                [field]: value,
            };
            // When switching to FREE_TEXT, clear selectedAnswerIds
            if (field === "questionType" && value === QuestionType.FREE_TEXT) {
                newValues.selectedAnswerIds = [];
            }
            // When switching to SYSTEM, clear answer set fields and set default expression
            if (field === "questionType" && value === QuestionType.SYSTEM) {
                newValues.selectedAnswerIds = [];
                newValues.answerSetId = null;
                if (!newValues.expression) {
                    newValues.expression = "CODE.original == true";
                }
            }
            // When switching to SPAN or SPANS, clear answer set fields (SPAN/SPANS have no answer set)
            if (field === "questionType" && (value === QuestionType.SPAN || value === QuestionType.SPANS)) {
                newValues.selectedAnswerIds = [];
                newValues.answerSetId = null;
            }
            // When switching away from SYSTEM, clear expression
            if (field === "questionType" && prev.questionType === QuestionType.SYSTEM && value !== QuestionType.SYSTEM) {
                newValues.expression = "";
            }
            return newValues;
        });
    };

    const handleSubmit = async () => {
        if (!formValues.name.trim() || !formValues.questionText.trim()) {
            setFormError("Name and question text are required");
            return;
        }
        // Validate answerSetId is required for FREE_TEXT questions
        if (formValues.questionType === QuestionType.FREE_TEXT && !formValues.answerSetId) {
            setFormError("Answer set is required for free text questions");
            return;
        }
        // Validate expression is required for SYSTEM questions
        if (formValues.questionType === QuestionType.SYSTEM && !formValues.expression.trim()) {
            setFormError("Expression is required for system questions");
            return;
        }
        // Validate: SPAN/SPANS questions should not have an answer set
        if ((formValues.questionType === QuestionType.SPAN || formValues.questionType === QuestionType.SPANS) && formValues.answerSetId) {
            setFormError("Answer set must not be provided for span questions");
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            // Ensure selectedAnswerIds is empty for FREE_TEXT and SPAN questions
            const selectedAnswerIds = (formValues.questionType === QuestionType.FREE_TEXT || formValues.questionType === QuestionType.SPAN || formValues.questionType === QuestionType.SPANS)
                ? [] : formValues.selectedAnswerIds;
            const payload = {
                name: formValues.name.trim(),
                questionText: formValues.questionText.trim(),
                questionType: formValues.questionType,
                required: formValues.required,
                answerSetId: (formValues.questionType === QuestionType.SPAN || formValues.questionType === QuestionType.SPANS) ? null : formValues.answerSetId,
                displayOrder: formValues.displayOrder,
                active: formValues.active,
                selectedAnswerIds,
                inclusionMode: formValues.inclusionMode,
                expression: formValues.questionType === QuestionType.SYSTEM ? formValues.expression.trim() : null,
            };
            if (selectedQuestion) {
                await updateQuestion(selectedQuestion.id, payload);
            } else {
                await createQuestion(payload);
            }
            await loadQuestions();
            handleBackToList();
        } catch (err) {
            console.error("Failed to save question", err);
            setFormError(err instanceof Error ? err.message : "Unable to save question");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const answerSetId = formValues.answerSetId;
        if (!answerSetId || answerSetCache[answerSetId]) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const detailed = await fetchAnswerSet(answerSetId);
                if (!cancelled) {
                    setAnswerSetCache((prev) => ({ ...prev, [answerSetId]: detailed }));
                }
            } catch (err) {
                console.error("Failed to load answer set", err);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [formValues.answerSetId, answerSetCache]);

    const selectedAnswerSet = formValues.answerSetId
        ? answerSetCache[formValues.answerSetId] ?? undefined
        : undefined;

    const answerPreview: AnswerValue[] = selectedAnswerSet?.answers ?? [];

    const handleDeleteQuestion = (question: Question) => {
        setDeleteConfirmation(question);
    };

    const confirmDeleteQuestion = async () => {
        if (!deleteConfirmation?.id) {
            setDeleteConfirmation(null);
            return;
        }
        const question = deleteConfirmation;
        setDeleteConfirmation(null);
        try {
            await deleteQuestion(question.id);
            await loadQuestions();
        } catch (err) {
            console.error("Failed to delete question", err);
            setError(err instanceof Error ? err.message : "Unable to delete question");
        }
    };

    const handleToggleActive = async (question: Question, active: boolean) => {
        if (!question.id) {
            return;
        }
        try {
            await updateQuestion(question.id, {
                name: question.name,
                questionText: question.questionText ?? "",
                questionType: question.questionType ?? QuestionType.MULTIPLE_CHOICE,
                required: question.required ?? false,
                answerSetId: question.answerSetId,
                active,
            });
            await loadQuestions();
        } catch (err) {
            console.error("Failed to toggle question active status", err);
            setError(err instanceof Error ? err.message : "Unable to update question");
        }
    };

    const handlePublishQuestion = async (question: Question) => {
        if (!question.id) {
            return;
        }
        setPublishingQuestionId(question.id);
        setError(null);
        try {
            await publishQuestion(question.id);
            await loadQuestions();
        } catch (err) {
            console.error("Failed to publish question", err);
            setError(err instanceof Error ? err.message : "Unable to publish question");
        } finally {
            setPublishingQuestionId(null);
        }
    };

    const renderActions = (question: Question) => (
        <>
            {question.status === "DRAFT" && (
                <Tooltip title="Publish question">
                    <IconButton
                        aria-label={`Publish question ${question.name}`}
                        disabled={publishingQuestionId === question.id}
                        onClick={(event) => {
                            event.stopPropagation();
                            handlePublishQuestion(question);
                        }}
                        sx={{ color: "#7498A6" }}
                    >
                        <Publish fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Edit">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleSelectQuestion(question);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    aria-label={`Delete question ${question.name}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteQuestion(question);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    const allAnswerIds = selectedAnswerSet?.answers
        ?.map((answer) => answer.id)
        .filter((id): id is number => id != null) ?? [];

    const isAnswerActive = (answer: AnswerValue) => {
        if (answer.id == null) {
            return false;
        }
        if (formValues.inclusionMode === InclusionMode.INCLUDE) {
            return formValues.selectedAnswerIds.includes(answer.id);
        } else {
            return !formValues.selectedAnswerIds.includes(answer.id);
        }
    };

    const areAllAnswersActive = allAnswerIds.length > 0
        && selectedAnswerSet?.answers
            ?.filter((answer) => answer.id != null)
            .every((answer) => isAnswerActive(answer)) === true;

    const handleAnswerToggle = (answer: AnswerValue) => {
        if (answer.id == null) {
            return;
        }
        const answerId = answer.id;
        const isInArray = formValues.selectedAnswerIds.includes(answerId);
        if (isInArray) {
            setFormValues((prev) => ({ ...prev, selectedAnswerIds: prev.selectedAnswerIds.filter((id) => id !== answerId) }));
        } else {
            setFormValues((prev) => ({ ...prev, selectedAnswerIds: [...prev.selectedAnswerIds, answerId] }));
        }
    };

    const handleAllAnswersToggle = (active: boolean) => {
        setFormValues((prev) => {
            const selectedAnswerIds = prev.inclusionMode === InclusionMode.INCLUDE
                ? (active ? allAnswerIds : [])
                : (active ? [] : allAnswerIds);

            return {
                ...prev,
                selectedAnswerIds,
            };
        });
    };

    const handleInclusionModeToggle = () => {
        setFormValues((prev) => {
            const newSelectedAnswerIds = allAnswerIds.filter((id) => !prev.selectedAnswerIds.includes(id));
            return {
                ...prev,
                selectedAnswerIds: newSelectedAnswerIds,
                inclusionMode: prev.inclusionMode === InclusionMode.INCLUDE ? InclusionMode.EXCLUDE : InclusionMode.INCLUDE,
            };
        });
    };

    const renderAnswerPreview = () => {
        if (!formValues.answerSetId) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Select an answer set to preview its values.
                </Typography>
            );
        }

        if (!selectedAnswerSet) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Loading answer set values...
                </Typography>
            );
        }

        if (!answerPreview.length) {
            return (
                <Typography variant="body2" color="text.secondary">
                    This answer set has no answers yet.
                </Typography>
            );
        }

        return (
            <Stack spacing={1}>
                {answerPreview.map((answer) => (
                    <Box
                        key={answer.id ?? answer.name}
                        sx={{
                            border: `1px solid ${theme.palette.grey[200]}`,
                            borderRadius: "3px",
                            px: 2,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            bgcolor: theme.palette.background.paper,
                        }}
                    >
                        <Typography variant="body2">{answer.name}</Typography>
                        <Switch
                            size="small"
                            inputProps={{ "aria-label": `Set ${answer.name ?? answer.displayText ?? "answer"} active for this question` }}
                            checked={isAnswerActive(answer)}
                            onChange={() => handleAnswerToggle(answer)}
                            disabled={answer.id == null}
                        />
                    </Box>
                ))}
            </Stack>
        );
    };

    const renderListView = () => (
        <RubricManagementList<Question>
            items={questions}
            totalCount={totalCount}
            loading={loading}
            error={error}
            listState={listState}
            columns={questionColumns}
            getItemKey={(item) => item.id}
            onItemClick={handleSelectQuestion}
            onAddClick={handleCreateNew}
            searchPlaceholder="Search questions by name..."
            addButtonLabel="Add Question"
            itemLabel="questions"
            emptyMessage="No questions found."
            renderActions={renderActions}
            statusFilter={statusFilter}
            onStatusFilterChange={(value: StatusFilter) => { setStatusFilter(value); listState.setPage(0); }}
            onToggleActive={handleToggleActive}
            isItemActive={(item) => Boolean(item.active)}
        />
    );

    const renderDetailView = () => (
        <Box>
            <Stack direction={{ xs: "column", md: "row" }} alignItems="center" spacing={2} mb={2}>
                <Button startIcon={<ArrowBack />} onClick={handleBackToList} variant="outlined">
                    Back to Questions
                </Button>
                <Box flexGrow={1} />
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleBackToList} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                        {saving ? "Saving..." : selectedQuestion ? "Update" : "Create"}
                    </Button>
                </Stack>
            </Stack>
            {formError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {formError}
                </Alert>
            )}
            {detailLoading ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress />
                </Stack>
            ) : (
                <Stack spacing={3}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            label="Name"
                            value={formValues.name}
                            onChange={(event) => handleFieldChange("name", event.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Question Text"
                            value={formValues.questionText}
                            onChange={(event) => handleFieldChange("questionText", event.target.value)}
                            required
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="question-type-label">Question Type</InputLabel>
                            <Select
                                labelId="question-type-label"
                                label="Question Type"
                                value={formValues.questionType}
                                onChange={(event) =>
                                    handleFieldChange("questionType", event.target.value as Question["questionType"])
                                }
                            >
                                {questionTypeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formValues.required}
                                    onChange={(event) => handleFieldChange("required", event.target.checked)}
                                />
                            }
                            label="Required"
                        />
                    </Stack>
                    {formValues.questionType !== QuestionType.SYSTEM && formValues.questionType !== QuestionType.SPAN && formValues.questionType !== QuestionType.SPANS && (
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                            <FormControl fullWidth>
                                <InputLabel id="answer-set-select-label">Answer Set</InputLabel>
                                <Select
                                    labelId="answer-set-select-label"
                                    label="Answer Set"
                                    value={formValues.answerSetId !== null ? String(formValues.answerSetId) : ""}
                                    onChange={(event) =>
                                        handleFieldChange(
                                            "answerSetId",
                                            event.target.value === "" ? null : Number(event.target.value)
                                        )
                                    }
                                    required={formValues.questionType === QuestionType.FREE_TEXT}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {answerSets.map((set) => (
                                        <MenuItem
                                            key={set.id}
                                            value={String(set.id)}
                                            disabled={set.active === false}
                                        >
                                            {set.name}
                                            {set.active === false && (
                                                <Chip
                                                    label="Inactive"
                                                    size="small"
                                                    color="warning"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    )}
                    {formValues.questionType === QuestionType.MULTIPLE_CHOICE && (
                        <>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formValues.inclusionMode === InclusionMode.EXCLUDE}
                                        onChange={handleInclusionModeToggle}
                                    />
                                }
                                label={`Automatically include new answers`}
                            />
                            <Divider />
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>
                                    Answer Preview
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            inputProps={{ "aria-label": "All answers active" }}
                                            checked={areAllAnswersActive}
                                            onChange={(event) => handleAllAnswersToggle(event.target.checked)}
                                            disabled={allAnswerIds.length === 0}
                                        />
                                    }
                                    label="All answers active"
                                />
                                {renderAnswerPreview()}
                            </Box>
                        </>
                    )}
                    {formValues.questionType === QuestionType.FREE_TEXT && (
                        <Typography variant="body2" color="text.secondary">
                            This is a free text question. Users will be able to enter text responses.
                        </Typography>
                    )}
                    {formValues.questionType === QuestionType.SPAN && (
                        <Typography variant="body2" color="text.secondary">
                            Users will select a single span annotation as their answer.
                        </Typography>
                    )}
                    {formValues.questionType === QuestionType.SPANS && (
                        <Typography variant="body2" color="text.secondary">
                            Users will select one or more span annotations as their answer.
                        </Typography>
                    )}
                    {formValues.questionType === QuestionType.SYSTEM && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                System questions are evaluated automatically based on a SpEL expression.
                                The expression determines which answer is selected based on code and work item context.
                            </Typography>
                            <ExpressionBuilder
                                expression={formValues.expression}
                                onChange={(expr) => handleFieldChange("expression", expr)}
                            />
                        </>
                    )}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleBackToList} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {saving ? "Saving..." : selectedQuestion ? "Update" : "Create"}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </Box>
    );

    return (
        <>
            {viewMode === "list" ? renderListView() : renderDetailView()}
            <ConfirmationDialog
                open={Boolean(deleteConfirmation)}
                title="Delete question?"
                description={
                    deleteConfirmation
                        ? `Delete question "${deleteConfirmation.name}"?`
                        : ""
                }
                confirmLabel="Delete"
                confirmColor="error"
                titleId="delete-question-title"
                descriptionId="delete-question-description"
                onCancel={() => setDeleteConfirmation(null)}
                onConfirm={confirmDeleteQuestion}
            />
        </>
    );
};

export default QuestionsPanel;
